import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  email: string;
  user_name: string;
}

interface EmailSelectorProps {
  label: string;
  placeholder: string;
  value: string[];
  onChange: (emails: string[]) => void;
  className?: string;
}

export const EmailSelector: React.FC<EmailSelectorProps> = ({
  label,
  placeholder,
  value,
  onChange,
  className = ''
}) => {
  const [input, setInput] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Load users from Supabase
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, email, user_name')
          .order('email');

        if (error) throw error;
        setUsers(data || []);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Filter users based on input
  useEffect(() => {
    if (!input.trim()) {
      setFilteredUsers([]);
      setShowDropdown(false);
      setSelectedIndex(-1);
      optionRefs.current = [];
      return;
    }

    const filtered = users.filter(user => 
      user.email.toLowerCase().includes(input.toLowerCase()) ||
      (user.user_name && user.user_name.toLowerCase().includes(input.toLowerCase()))
    ).filter(user => !value.includes(user.email)); // Exclude already selected

    setFilteredUsers(filtered);
    setShowDropdown(filtered.length > 0);
    setSelectedIndex(-1); // Reset selection when filtering
    optionRefs.current = new Array(filtered.length).fill(null); // Reset refs array
  }, [input, users, value]);

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && optionRefs.current[selectedIndex]) {
      optionRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex]);

  const addEmail = (email: string) => {
    if (email && !value.includes(email)) {
      onChange([...value, email]);
      setInput('');
      setShowDropdown(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    }
  };

  const removeEmail = (emailToRemove: string) => {
    onChange(value.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) {
      // Normal behavior when dropdown is closed
      if (e.key === 'Enter' && input.trim()) {
        e.preventDefault();
        // If it's a valid email format, add it directly
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(input.trim())) {
          addEmail(input.trim());
        }
      } else if (e.key === 'Backspace' && !input && value.length > 0) {
        // Remove last email if input is empty
        removeEmail(value[value.length - 1]);
      }
      return;
    }

    // Keyboard navigation when dropdown is open
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredUsers.length) {
          addEmail(filteredUsers[selectedIndex].email);
        } else if (input.trim()) {
          // If no item selected but there's input, try to add as email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (emailRegex.test(input.trim())) {
            addEmail(input.trim());
          } else if (filteredUsers.length > 0) {
            // Add first filtered user
            addEmail(filteredUsers[0].email);
          }
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
        
      case 'Tab':
        // Allow normal tab behavior
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
        
      case 'Backspace':
        if (!input && value.length > 0) {
          // Remove last email if input is empty
          removeEmail(value[value.length - 1]);
        }
        break;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      
      <div className="relative" ref={dropdownRef}>
        {/* Email tags and input */}
        <div className="min-h-[40px] border border-gray-300 rounded-md px-3 py-2 flex flex-wrap gap-1 items-center bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          {value.map((email, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
            >
              <Mail className="h-3 w-3" />
              {email}
              <button
                type="button"
                onClick={() => removeEmail(email)}
                className="text-blue-600 hover:text-blue-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none text-sm"
            onFocus={() => input && setShowDropdown(filteredUsers.length > 0)}
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Đang tải...</div>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <button
                  key={user.id}
                  ref={(el) => optionRefs.current[index] = el}
                  type="button"
                  onClick={() => addEmail(user.email)}
                  className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                    index === selectedIndex 
                      ? 'bg-blue-100 text-blue-900' 
                      : 'hover:bg-gray-100'
                  }`}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseLeave={() => setSelectedIndex(-1)}
                >
                  <Mail className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium">{user.email}</div>
                    {user.user_name && (
                      <div className="text-xs text-gray-500">{user.user_name}</div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                Không tìm thấy user nào
              </div>
            )}
          </div>
        )}
      </div>
      
      {value.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          {value.length} email được chọn
        </div>
      )}
    </div>
  );
};